<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const REPLY_DEFAULT = 'Fiyatı DM’den ilettik, kutunuza bakın 💌';
const PRICE_RE = /fiyat|kaç|kac|ne kadar|fiat|price|tl|₺|beden|stok/i;
const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-green-600',
  'bg-red-500',
  'bg-woot-600',
  'bg-violet-700',
  'bg-green-500',
  'bg-woot-500',
  'bg-red-600',
];
const colorFor = name => {
  const s = (name || '?').split('').reduce((a, ch) => a + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[s % AVATAR_COLORS.length];
};

const priceText = pr =>
  pr.price_max && pr.price_max !== pr.price_min
    ? `${Math.round(pr.price_min)}–${Math.round(pr.price_max)} TL`
    : `${Math.round(pr.price_min)} TL`;

const composeDm = (post, pr) =>
  pr && pr.found
    ? `Merhaba 🌸\n“${pr.title}” ürünümüzün fiyatı ${priceText(pr)} 💕\n\n🔗 ${post.permalink}\n\nSipariş ve detaylar için buradan yazabilirsiniz, beklerim 💕`
    : `Merhaba 🌸 Fiyat ve sipariş bilgisi için buradayım, hangi ürünü sormuştunuz? 💕\n🔗 ${post.permalink}`;

const source = ref('instagram');
const posts = ref([]);
const channels = ref([]);
const brand = ref('all');
const loading = ref(false);
const errorMsg = ref('');

const selected = ref(null);
const comments = ref([]);
const commentsLoading = ref(false);
const commentsError = ref('');
const sendingId = ref(null);
const muted = ref(true);

const loadPosts = async () => {
  loading.value = true;
  errorMsg.value = '';
  posts.value = [];
  try {
    const res = await fetchMarketplaceJson(
      accountId,
      `social/posts?source=${source.value}`
    );
    posts.value = res.posts || [];
    channels.value = res.channels || [];
    if (res.error) errorMsg.value = res.error;
  } catch (e) {
    errorMsg.value = e.message || 'Gönderiler alınamadı.';
  } finally {
    loading.value = false;
  }
};

const switchSource = s => {
  if (source.value === s) return;
  source.value = s;
  brand.value = 'all';
  loadPosts();
};

const filteredPosts = computed(() =>
  brand.value === 'all'
    ? posts.value
    : posts.value.filter(p => p.channel === brand.value)
);

const priceInfo = ref(null);

const auto = ref({
  enabled: false,
  handled_total: 0,
  last_run: null,
  enabled_at: null,
});
const togglingAuto = ref(false);

const loadAuto = async () => {
  try {
    auto.value = await fetchMarketplaceJson(accountId, 'social/autoreply');
  } catch (e) {
    /* sessiz */
  }
};

const toggleAuto = async () => {
  const next = !auto.value.enabled;
  if (
    next &&
    !window.confirm(
      'Otomatik yanıt AÇILSIN mı?\n\nBundan sonra gelen “fiyat” yorumlarına otomatik olarak herkese açık cevap + fiyat DM’i gönderilecek. Eski yorumlara dokunulmaz.'
    )
  ) {
    return;
  }
  togglingAuto.value = true;
  try {
    auto.value = await postMarketplaceJsonBody(accountId, 'social/autoreply', {
      enabled: next,
    });
  } catch (e) {
    /* sessiz */
  } finally {
    togglingAuto.value = false;
  }
};

const openPost = async post => {
  selected.value = post;
  muted.value = true;
  comments.value = [];
  commentsError.value = '';
  commentsLoading.value = true;
  priceInfo.value = null;

  // Gönderinin ürün fiyatını sorgula (DM metnine yedir).
  let dmDefault = composeDm(post, null);
  try {
    const pr = await fetchMarketplaceJson(
      accountId,
      `social/product_price?caption=${encodeURIComponent(post.caption || '')}`
    );
    priceInfo.value = pr;
    if (pr && pr.found) dmDefault = composeDm(post, pr);
  } catch (e) {
    /* fiyat bulunamazsa genel metin kalır */
  }

  try {
    const res = await fetchMarketplaceJson(
      accountId,
      `social/comments?source=${source.value}&media_id=${post.id}&channel=${post.channel}`
    );
    comments.value = (res.comments || []).map(c => ({
      ...c,
      dmText: dmDefault,
      replyText: REPLY_DEFAULT,
      mode: '',
      done: '',
    }));
    if (res.error) commentsError.value = res.error;
  } catch (e) {
    commentsError.value = e.message || 'Yorumlar alınamadı.';
  } finally {
    commentsLoading.value = false;
  }
};

const closeModal = () => {
  selected.value = null;
};

const sendDm = (c, message) =>
  postMarketplaceJsonBody(accountId, 'social/dm', {
    comment_id: c.id,
    channel: selected.value.channel,
    source: source.value,
    message,
  });

const send = async (c, kind) => {
  const text = kind === 'dm' ? c.dmText : c.replyText;
  const label =
    kind === 'dm'
      ? 'DM gönderilsin mi?'
      : 'Müşteriye açık cevap + otomatik fiyat DM’i gönderilsin mi?';
  if (!window.confirm(`${label}\n\n"${text}"`)) return;
  sendingId.value = `${kind}-${c.id}`;
  try {
    if (kind === 'dm') {
      const res = await sendDm(c, text);
      c.done = res.ok
        ? '✅ DM gönderildi'
        : `⚠️ ${res.error || 'Gönderilemedi'}`;
      if (res.ok) c.mode = '';
    } else {
      // 1) herkese açık cevap → 2) arkasından otomatik fiyat DM'i (4 haneli koddan)
      const reply = await postMarketplaceJsonBody(accountId, 'social/reply', {
        comment_id: c.id,
        channel: selected.value.channel,
        source: source.value,
        message: text,
      });
      if (!reply.ok) {
        c.done = `⚠️ ${reply.error || 'Cevap gönderilemedi'}`;
      } else {
        const dm = await sendDm(c, c.dmText);
        c.done = dm.ok
          ? '✅ Cevap yayınlandı + fiyat DM’i gönderildi 💌'
          : `✅ Cevap yayınlandı · ⚠️ DM gitmedi (${dm.error || '?'})`;
        c.mode = '';
      }
    }
  } catch (e) {
    c.done = `⚠️ ${e.message}`;
  } finally {
    sendingId.value = null;
  }
};

const fmtDate = iso =>
  iso
    ? new Date(iso).toLocaleString('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '';

const initials = name => {
  const clean = (name || '').replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ]/gi, '');
  return (clean ? clean.slice(0, 2) : '?').toUpperCase();
};

onMounted(() => {
  loadPosts();
  loadAuto();
});
</script>

<template>
  <div class="w-full h-full overflow-auto bg-n-background">
    <div class="p-6 max-w-[1200px] mx-auto">
      <div class="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 class="text-2xl font-black text-n-slate-12">
          Sosyal Medya Yorumları
        </h1>
        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 rounded-xl text-base font-bold border-2 transition disabled:opacity-50"
            :class="
              auto.enabled
                ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                : 'bg-n-solid-1 text-n-slate-12 border-n-slate-5 hover:bg-n-solid-2'
            "
            :disabled="togglingAuto"
            @click="toggleAuto"
          >
            <span
              :class="auto.enabled ? 'i-lucide-bot' : 'i-lucide-power-off'"
            />
            Otomatik Yanıt: {{ auto.enabled ? 'AÇIK' : 'Kapalı' }}
          </button>
          <button
            class="px-4 py-2 rounded-xl text-base font-semibold bg-n-solid-2 text-n-slate-12 border border-n-slate-5 hover:bg-n-solid-3 disabled:opacity-50"
            :disabled="loading"
            @click="loadPosts"
          >
            <span class="i-lucide-rotate-cw" /> Yenile
          </button>
        </div>
      </div>

      <!-- OTOMATİK YANIT DURUMU -->
      <div
        v-if="auto.enabled"
        class="mb-4 px-3 py-2 rounded-xl bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800 text-sm text-green-800 dark:text-green-300 flex items-center gap-2 flex-wrap"
      >
        <span class="i-lucide-bot text-base" />
        <span class="font-bold">Otomatik yanıt çalışıyor</span> · yeni “fiyat”
        yorumlarına otomatik cevap + fiyat DM’i gidiyor · şu ana dek
        <b>{{ auto.handled_total }}</b> yanıt
        <span v-if="auto.last_run"
          >· son tarama: {{ fmtDate(auto.last_run) }}</span
        >
      </div>

      <!-- KAYNAK SEKMELERİ -->
      <div class="flex items-center gap-2 mb-3">
        <button
          class="px-4 py-2 rounded-xl text-base font-bold border-2 transition"
          :class="
            source === 'instagram'
              ? 'bg-violet-600 text-white border-violet-600'
              : 'bg-n-solid-1 text-n-slate-11 border-n-slate-5 hover:bg-n-solid-2'
          "
          @click="switchSource('instagram')"
        >
          📷 Instagram
        </button>
        <button
          class="px-4 py-2 rounded-xl text-base font-bold border-2 transition"
          :class="
            source === 'facebook'
              ? 'bg-woot-600 text-white border-woot-600'
              : 'bg-n-solid-1 text-n-slate-11 border-n-slate-5 hover:bg-n-solid-2'
          "
          @click="switchSource('facebook')"
        >
          👍 Facebook
        </button>
      </div>

      <!-- MARKA FİLTRESİ -->
      <div
        v-if="channels.length > 1"
        class="flex items-center gap-2 mb-4 flex-wrap"
      >
        <button
          class="px-3 py-1.5 rounded-lg text-sm font-semibold border"
          :class="
            brand === 'all'
              ? 'bg-n-slate-12 text-n-solid-1 border-n-slate-12'
              : 'bg-n-solid-1 text-n-slate-11 border-n-slate-5'
          "
          @click="brand = 'all'"
        >
          Tümü
        </button>
        <button
          v-for="ch in channels"
          :key="ch.id"
          class="px-3 py-1.5 rounded-lg text-sm font-semibold border"
          :class="
            brand === ch.id
              ? 'bg-n-slate-12 text-n-solid-1 border-n-slate-12'
              : 'bg-n-solid-1 text-n-slate-11 border-n-slate-5'
          "
          @click="brand = ch.id"
        >
          {{ ch.name }}
        </button>
      </div>

      <p v-if="loading" class="text-lg text-n-slate-11 py-10 text-center">
        Gönderiler yükleniyor…
      </p>

      <!-- FACEBOOK İZİN UYARISI / HATA -->
      <div
        v-else-if="errorMsg && !posts.length"
        class="rounded-2xl p-6 bg-yellow-50 border-2 border-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-700"
      >
        <p class="text-lg font-bold text-yellow-800 dark:text-yellow-300 mb-1">
          {{
            source === 'facebook'
              ? 'Facebook içeriği için izin gerekli'
              : 'Gönderiler alınamadı'
          }}
        </p>
        <p class="text-base text-n-slate-11">
          {{
            source === 'facebook'
              ? 'Facebook sayfası bağlı ama gönderi/yorum okuma izni (pages_read_engagement) henüz verilmemiş. Meta tarafında bu izin verilince Facebook yorumları da burada görünecek.'
              : errorMsg
          }}
        </p>
      </div>

      <!-- GÖNDERİ IZGARASI -->
      <div v-else class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <button
          v-for="post in filteredPosts"
          :key="post.id"
          class="group text-left rounded-xl overflow-hidden border-2 border-n-slate-5 bg-n-solid-1 hover:border-woot-400 hover:shadow-lg transition"
          @click="openPost(post)"
        >
          <div class="relative aspect-square bg-n-solid-3">
            <img
              v-if="post.thumbnail"
              :src="post.thumbnail"
              class="w-full h-full object-cover"
              loading="lazy"
              alt=""
            />
            <span
              class="absolute top-1 left-1 text-xs px-1.5 py-0.5 rounded-md font-bold text-white"
              :class="post.is_reels ? 'bg-violet-600' : 'bg-woot-600'"
            >
              {{
                post.is_reels
                  ? 'Reels'
                  : post.source === 'facebook'
                    ? 'FB'
                    : 'Post'
              }}
            </span>
            <span
              class="absolute bottom-1 right-1 text-xs px-1.5 py-0.5 rounded-md font-bold text-white bg-black/60"
            >
              💬 {{ post.comments_count }}
            </span>
          </div>
          <div class="p-2">
            <div class="text-sm font-bold text-n-slate-12 truncate">
              {{ post.caption || '(açıklama yok)' }}
            </div>
            <div class="text-xs text-n-slate-10 truncate">
              {{ post.channel_name }} · ❤️ {{ post.like_count }} ·
              {{ fmtDate(post.timestamp) }}
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- ===== INSTAGRAM TARZI MODAL ===== -->
    <div
      v-if="selected"
      class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3"
      @click.self="closeModal"
    >
      <div
        class="bg-n-solid-1 rounded-2xl overflow-hidden w-full max-w-4xl h-[90vh] flex flex-col md:flex-row shadow-2xl"
      >
        <!-- SOL: medya — Instagram gibi direkt oynar, kontrol çubuğu yok -->
        <div
          class="md:w-[58%] bg-black flex items-center justify-center relative min-h-[260px]"
        >
          <video
            v-if="selected.is_video && selected.media_url"
            :key="selected.id"
            :src="selected.media_url"
            :poster="selected.thumbnail"
            autoplay
            loop
            playsinline
            :muted="muted"
            class="h-full w-full object-contain"
          />
          <img
            v-else-if="selected.thumbnail"
            :src="selected.thumbnail"
            class="h-full w-full object-contain"
            alt=""
          />
          <span v-else class="text-n-slate-8">Görsel yok</span>

          <button
            v-if="selected.is_video && selected.media_url"
            class="absolute bottom-3 right-3 size-9 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/80"
            @click="muted = !muted"
          >
            <span
              :class="muted ? 'i-lucide-volume-x' : 'i-lucide-volume-2'"
              class="text-lg"
            />
          </button>
        </div>

        <!-- SAĞ: tek sütun — başlık sabit, açıklama + yorumlar BİRLİKTE akar -->
        <div class="md:w-[42%] flex flex-col min-h-0 bg-n-solid-1">
          <!-- sabit başlık -->
          <div
            class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-slate-3 shrink-0"
          >
            <div class="flex items-center gap-2.5 min-w-0">
              <div
                class="size-9 rounded-full p-[2px] bg-gradient-to-tr from-yellow-400 via-violet-500 to-violet-600 shrink-0"
              >
                <div
                  class="size-full rounded-full bg-n-solid-1 flex items-center justify-center text-xs font-bold text-n-slate-12"
                >
                  {{ initials(selected.channel_name) }}
                </div>
              </div>
              <div class="font-bold text-n-slate-12 truncate">
                {{ selected.channel_name }}
              </div>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <a
                :href="selected.permalink"
                target="_blank"
                rel="noopener noreferrer"
                class="px-2 py-1 rounded-lg text-xs font-semibold bg-n-solid-2 text-n-slate-12 border border-n-slate-5 hover:bg-n-solid-3"
                >Aç ↗</a
              >
              <button
                class="size-8 rounded-lg hover:bg-n-solid-3 text-n-slate-11 flex items-center justify-center"
                @click="closeModal"
              >
                <span class="i-lucide-x text-lg" />
              </button>
            </div>
          </div>

          <!-- TEK AKAN SÜTUN: açıklama + yorumlar -->
          <div class="flex-1 overflow-auto min-h-0">
            <!-- açıklama: akışın ilk öğesi (ayrı kutu değil) -->
            <div
              v-if="selected.caption"
              class="flex gap-3 px-4 py-3 border-b border-n-slate-3"
            >
              <div
                class="size-8 rounded-full p-[2px] bg-gradient-to-tr from-yellow-400 via-violet-500 to-violet-600 shrink-0"
              >
                <div
                  class="size-full rounded-full bg-n-solid-1 flex items-center justify-center text-[10px] font-bold text-n-slate-12"
                >
                  {{ initials(selected.channel_name) }}
                </div>
              </div>
              <div class="min-w-0">
                <div
                  class="text-base text-n-slate-12 leading-relaxed whitespace-pre-line"
                >
                  <span class="font-bold mr-1">{{ selected.channel_name }}</span
                  >{{ selected.caption }}
                </div>
                <div class="text-xs text-n-slate-10 mt-1">
                  {{ fmtDate(selected.timestamp) }}
                </div>
              </div>
            </div>

            <!-- yorumlar -->
            <div class="px-4 py-3 space-y-4">
              <p
                v-if="commentsLoading"
                class="text-base text-n-slate-11 text-center py-6"
              >
                Yorumlar yükleniyor…
              </p>
              <p
                v-else-if="commentsError"
                class="text-base text-ruby-700 text-center py-6"
              >
                {{ commentsError }}
              </p>
              <p
                v-else-if="!comments.length"
                class="text-base text-n-slate-10 text-center py-6"
              >
                Henüz yorum yok.
              </p>

              <div v-for="c in comments" :key="c.id" class="flex gap-3">
                <div
                  class="size-9 rounded-full text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-sm"
                  :class="colorFor(c.username)"
                >
                  {{ initials(c.username) }}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="text-base text-n-slate-12 leading-snug">
                    <span class="font-bold mr-1">{{ c.username }}</span
                    >{{ c.text }}
                    <span
                      v-if="PRICE_RE.test(c.text || '')"
                      class="ml-1 text-xs px-1.5 py-0.5 rounded-md bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 font-bold align-middle"
                      >🏷️ fiyat</span
                    >
                  </div>
                  <div
                    class="flex items-center gap-3 text-xs text-n-slate-10 mt-1"
                  >
                    <span>{{ fmtDate(c.timestamp) }}</span>
                    <span v-if="c.like_count">❤️ {{ c.like_count }}</span>
                  </div>
                  <div
                    v-for="r in c.replies"
                    :key="r.id"
                    class="text-sm text-n-slate-11 mt-1.5 pl-3 border-l-2 border-n-slate-4 leading-snug"
                  >
                    <b class="mr-1">{{ r.username }}</b>{{ r.text }}
                  </div>

                  <!-- aksiyonlar (modern) -->
                  <div class="flex flex-wrap gap-2 mt-2">
                    <button
                      class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-violet-600 hover:from-violet-700 hover:to-violet-700 shadow-sm transition hover:scale-[1.03]"
                      @click="c.mode = c.mode === 'dm' ? '' : 'dm'"
                    >
                      <span class="i-lucide-sparkles" /> Fiyatı DM'le
                    </button>
                    <button
                      class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold text-n-slate-12 bg-n-solid-1 border border-n-slate-5 hover:border-violet-400 hover:text-violet-600 transition"
                      @click="c.mode = c.mode === 'reply' ? '' : 'reply'"
                    >
                      <span class="i-lucide-corner-down-left" /> Müşteriyi
                      Cevapla
                    </button>
                  </div>

                  <div v-if="c.mode === 'dm'" class="mt-2">
                    <div
                      class="text-xs font-semibold mb-1 px-2 py-1 rounded-lg inline-block"
                      :class="
                        priceInfo && priceInfo.found
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      "
                    >
                      <template v-if="priceInfo && priceInfo.found">
                        🏷️ Otomatik bulundu: {{ priceText(priceInfo) }} (kod
                        {{ priceInfo.code }})
                      </template>
                      <template v-else>
                        ⚠️ Fiyat otomatik bulunamadı — mesajı kontrol et
                      </template>
                    </div>
                    <textarea
                      v-model="c.dmText"
                      rows="5"
                      class="w-full text-sm rounded-lg border-2 border-violet-300 bg-n-solid-1 text-n-slate-12 px-2 py-1.5"
                    />
                    <button
                      class="mt-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-violet-600 hover:from-violet-700 hover:to-violet-700 disabled:opacity-50"
                      :disabled="sendingId === `dm-${c.id}`"
                      @click="send(c, 'dm')"
                    >
                      <span class="i-lucide-send" />
                      {{
                        sendingId === `dm-${c.id}` ? 'Gönderiliyor…' : 'Gönder'
                      }}
                    </button>
                    <span class="text-xs text-n-slate-10 ml-2"
                      >yorum başına 1 kez, 7 gün içinde</span
                    >
                  </div>

                  <div v-if="c.mode === 'reply'" class="mt-2">
                    <textarea
                      v-model="c.replyText"
                      rows="2"
                      class="w-full text-sm rounded-lg border-2 border-n-slate-5 bg-n-solid-1 text-n-slate-12 px-2 py-1.5"
                    />
                    <div
                      class="text-xs mt-1 px-2 py-1 rounded-lg inline-block bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 font-semibold"
                    >
                      <template v-if="priceInfo && priceInfo.found">
                        🪄 Cevapla → ardından fiyat DM'i otomatik gider:
                        {{ priceText(priceInfo) }} + link
                      </template>
                      <template v-else>
                        🪄 Cevapla → ardından otomatik DM gider (fiyat
                        bulunamadı, genel mesaj + link)
                      </template>
                    </div>
                    <button
                      class="mt-1 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 whitespace-nowrap shadow-sm"
                      :disabled="sendingId === `reply-${c.id}`"
                      @click="send(c, 'reply')"
                    >
                      <span class="i-lucide-corner-down-left shrink-0" />
                      {{
                        sendingId === `reply-${c.id}`
                          ? 'Gönderiliyor…'
                          : 'Cevapla + Fiyat DM’i'
                      }}
                    </button>
                  </div>

                  <div
                    v-if="c.done"
                    class="mt-1 text-sm font-semibold"
                    :class="
                      c.done.startsWith('✅')
                        ? 'text-green-700'
                        : 'text-ruby-700'
                    "
                  >
                    {{ c.done }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
